import React from 'react';
import { Accordion, Card, Row, Col, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../css/DailyAnalysis.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend);

function DailyAnalysis({ dailyAnalysis }) {
    const smaData = {
        labels: ['10 Day', '30 Day', '50 Day', '150 Day', '200 Day'],
        datasets: [
            {
                label: 'Simple Moving Averages',
                data: [
                    dailyAnalysis.sma_ten_day,
                    dailyAnalysis.sma_thirty_day,
                    dailyAnalysis.sma_fifty_day,
                    dailyAnalysis.sma_hundred_day,
                    dailyAnalysis.sma_two_hundred_day
                ],
                backgroundColor: '#C1DFF0',
                borderColor: 'rgba(75,192,192,1)',
                borderWidth: 1,
            },
        ],
    };

    const greenDotData = {
        labels: dailyAnalysis.green_dot_dates.map(date => new Date(date).toLocaleDateString()),
        datasets: [
            {
                label: 'Green Dot Values',
                data: dailyAnalysis.green_dot_values,
                backgroundColor: '#C1DFF0',
                borderColor: 'rgba(153,102,255,1)',
                borderWidth: 1,
            },
        ],
    };

    return (
        <Container>
            <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Statistical Analysis Overview</Accordion.Header>
                    <Accordion.Body>
                        <Row className="my-4">
                            <Col>
                                <Card className="shadow-sm">
                                    <Card.Header as="h5" className="text-center bg-primary text-white">General Analysis</Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <Card.Text>
                                                    <strong>Symbol:</strong> {dailyAnalysis.symbol}
                                                </Card.Text>
                                                {dailyAnalysis.ml_confidence !== null && (
                                                <><Card.Text>
                                                        <strong>ML Confidence:</strong> {dailyAnalysis.ml_confidence.toFixed(2)}%
                                                    </Card.Text><Card.Text>
                                                            <strong>ML Predictions:</strong>
                                                            <ul>
                                                                {dailyAnalysis.ml_predictions.map((prediction, index) => (
                                                                    <li key={index}>
                                                                        <strong>{prediction.type}:</strong> {prediction.value}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </Card.Text></>
                                                )}
                                                <Card.Text>
                                                    <strong>Analysis Date:</strong> {new Date(dailyAnalysis.day).toLocaleDateString()}
                                                </Card.Text>
                                                <Card.Text>
                                                    <strong>Potential Buy Signal: </strong>
                                                    {dailyAnalysis.simple_stat_buy_signal ? 'Yes' : 'No'}
                                                    <OverlayTrigger
                                                        className='tooltip-inner'
                                                        placement="auto"
                                                        overlay={<Tooltip id="tooltip-top">The <strong> Potential Buy Signal</strong> is determined by the following criteria:
                                                            <ol className='tooltip-inner'>
                                                                <li>Current Price is greater than the 150 day simple moving average (SMA) which is greater than 200 day SMA</li>
                                                                <li>150 day SMA {'>'} 200 day moving average</li>
                                                                <li>200 day SMA has been trending for 1 month</li>
                                                                <li>Current Price is greater than the 50 day SMA</li>
                                                                <li>Current Price is 30% Greater than 52 week low</li>
                                                                <li>Current price is within 25% of 52 week high</li>
                                                                <li>RSI (see below) is greater than 70</li>
                                                            </ol>
                                                        </Tooltip>}
                                                        trigger={['hover', 'focus']}
                                                        rootClose
                                                        container={document.body}
                                                    >
                                                        <i className="bi bi-info-circle" style={{ marginLeft: '5px', cursor: 'pointer', fontSize: '0.8em' }}></i>
                                                    </OverlayTrigger>
                                                </Card.Text>
                                                <Card.Text>
                                                    <strong>RSI:</strong> {dailyAnalysis.rsi.toFixed(2)}
                                                </Card.Text>
                                            </Col>
                                            <Col md={6}>
                                                <Line data={smaData} />
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            <br />
            <Accordion defaultActiveKey={null}>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Simple Moving Average Plot</Accordion.Header>
                    <Accordion.Body>
                        <Row>
                            <Col md={12}>
                                <Card className="shadow-sm">
                                    <Card.Body className='text-left'>
                                        {dailyAnalysis.sma_plot && <img src={`data:image/png;base64,${dailyAnalysis.sma_plot}`} alt="SMA Plot" className="img-fluid" />}
                                        <Card.Text className="text-left">
                                            <br />
                                            Above is a visualization providing buy and sell indicators using SMAs (30 day vs 100 day window).
                                            As the shorter SMA (30) rises above the longer (100) SMA, this is a signal to buy meanwhile the opposite can
                                            be said when the shorter SMA (30) falls below the longer (100) SMA. You can also see some of these crossing points
                                            using smaller SMAs in the first graph on this page. The <strong>Sell</strong> indicator isn't the strongest;
                                            however, using this for long term buys could prove successful. <br />
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            <br />
            <Accordion defaultActiveKey={null}>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Support Vector Regression</Accordion.Header>
                    <Accordion.Body>
                        <Row>
                            <Col md={12}>
                                <Card className="shadow-sm">
                                    <Card.Body className='text-left'>
                                        {dailyAnalysis.svr_plot && <img src={`data:image/png;base64,${dailyAnalysis.svr_plot}`} alt="SVR Plot" className="img-fluid" />}
                                        <Card.Text className="text-left">    <br />
                                            The plot above shows the SVR (Support Vector Regression) machine learning model which is a type of supervised learning algorithm that
                                            analyzes data for regression analysis. This model is used to predict continuous values or the value of the stock on a specific day.
                                            The SVR model is used to predict stock prices based on historical data. <br /><br />
                                            Typically, the RBF kernel/model is most accurate using 65% of data to train and 35% to test with the past 30
                                            days of data. The SVR model is used to predict stock prices based on historical data. <br />
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            <br />
            <Accordion defaultActiveKey={null}>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Green Dot Indicators (GDI)</Accordion.Header>
                    <Accordion.Body>
                        <Row>
                            <Col md={12}>
                                <Card>
                                    <Card.Body>
                                        <Card.Title>Green Dot Values</Card.Title>
                                        <Line data={greenDotData} />
                                        <Card.Text className="text-left">
                                            <div >
                                            Green Dot Indicators (GDIs) are used to identify potential entry points during both long-term and short-term uptrends. The optimal buying window typically falls within 1–3 days after a green dot appears. This signal is generated when the 10.4 stochastic crosses above the 4-day SMA following an oversold condition, indicating a potential reversal and buying opportunity.
                                            The 10.4 stochastic is a specific configuration of the stochastic oscillator, a popular technical indicator used to measure momentum and identify potential overbought or oversold conditions in a stock or other asset. Here's what the components mean:

                                            10-period lookback (the %K line):
                                            The "10" refers to the number of past periods (days, hours, etc.) used to calculate the stochastic oscillator. It measures the current price relative to the range of prices (highs and lows) over the last 10 periods. This creates the %K line, which reflects the raw stochastic value.
                                            4-period smoothing (the %D line):
                                            The "4" represents a simple moving average (SMA) applied to the %K line to create the %D line. This smooths out short-term fluctuations in the raw stochastic value, making it easier to interpret.
                                            How It Works:
                                            The stochastic oscillator compares the closing price to the price range over the lookback period. Its values oscillate between 0 and 100, with:
                                            Overbought conditions typically above 80 (indicating a potential reversal or pause in upward momentum).
                                            Oversold conditions typically below 20 (indicating a potential reversal or pause in downward momentum). 
                                            <a target="_blank" rel="noreferrer noopener" href="https://wishingwealthblog.com/2018/03/green-dot-strategy-defined/">More Info</a>
                                            </div>
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            <br />
            <Accordion defaultActiveKey={null}>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>General Statistics</Accordion.Header>
                    <Accordion.Body>
                        <Row>
                            <Col md={12}>
                                <Card>
                                    <Card.Body className='text-left'>
                                        {dailyAnalysis.plot && <img src={`data:image/png;base64,${dailyAnalysis.plot}`} alt="Plot" className="img-fluid" />}
                                        <Card.Text className="text-left">
                                            <br />
                                            The plot above illustrates daily data from the past year, highlighting key peaks, Simple Moving Averages (SMA), and Bollinger Bands. SMAs are a valuable tool for identifying upward or downward trends in a stock, particularly when calculated over different time intervals. This plot features 10-day, 30-day, and 50-day moving averages, while an example below incorporates 30-day, 100-day, and 200-day averages for broader trend analysis.

                                            Bollinger Bands, developed by John Bollinger, are volatility bands positioned above and below a moving average. These bands adjust dynamically based on market volatility, with their width determined by standard deviation. As volatility increases, the bands widen, and as it decreases, the bands contract, providing insight into market conditions. <br />
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            <br />
            <Accordion defaultActiveKey={null}>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Decision Tree</Accordion.Header>
                    <Accordion.Body>
                        <Row>
                            <Col md={12}>
                                <Card>
                                    <Card.Body className='text-left'>
                                        {dailyAnalysis.decision_tree_plot && <img src={`data:image/png;base64,${dailyAnalysis.decision_tree_plot}`} alt="Decision Tree Plot" className="img-fluid" />}
                                        <br />
                                        <Card.Text className="text-left">
                                        The key difference between classification and regression decision trees lies in the type of values they handle. Classification decision trees work with unordered, categorical dependent variables, while regression decision trees deal with ordered, continuous values.

                                        Decision trees create regression or classification models by organizing data in a hierarchical tree structure. The process involves repeatedly splitting the dataset into smaller subsets, gradually constructing the tree. The resulting tree consists of decision nodes and leaf nodes. A decision node has two or more branches, while a leaf node represents a final prediction or decision. The root node, located at the top of the tree, serves as the best predictor for the dataset.

                                        Decision trees are versatile and can handle both categorical and numerical data. For example, when predicting stock market movements, a decision tree regressor model, along with a linear regression model, can be applied by using 75% of the data for training and 25% for testing.
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            <br />
            <Accordion defaultActiveKey={null}>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Relative Strength Index (RSI)</Accordion.Header>
                    <Accordion.Body>
                        <Row>
                            <Col md={12}>
                                <Card>
                                    <Card.Body className='text-left'>
                                        {dailyAnalysis.rsi_plot && <img src={`data:image/png;base64,${dailyAnalysis.rsi_plot}`} alt="RSI Plot" className="img-fluid" />}
                                        <br />
                                        <Card.Text className="text-left">
                                            The Relative Strength Index (RSI) is a technical indicator used to evaluate whether a stock is overbought or oversold. It calculates the ratio of average daily gains to average daily losses over a specific period and normalizes this data to a scale of 0 to 100. Typically, an RSI above 70 suggests that a stock may be overbought, indicating a potential for a price correction. Conversely, an RSI below 30 indicates the stock may be oversold, suggesting it could be undervalued and potentially poised for a rebound.
                                            <br />
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </Container>
    );
}

export default DailyAnalysis;          